import React, { useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getSectionWisePaidSummary } from "../../api/feeApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `fee-report-section-summary-paid-list`
 * ("Fee Paid Summary") — institute-wide real paid invoices in a date range,
 * pivoted section × fee-head, with a toggle between "Class Wise" and
 * "Class-Shift-Section Wise" grouping (the old app's own two query variants).
 * Confirmed via `FeeReportService.sectionWisePaidSummary`'s two native
 * queries that this needs real backend (a per-section, per-fee-head SUM over
 * every student's invoices institute-wide, not derivable from one student's
 * or one section's already-exposed data) — new `GET
 * /api/fee-reports/section-paid-summary`. Note: the old app's own controller
 * silently drops an `academicYear` param the frontend UI sends — this report
 * is NOT filtered by academic year in the old app, only by payment-date
 * range — preserved here for fidelity.
 */
export default function FeeSectionSummaryPaidListPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [byShiftAndSection, setByShiftAndSection] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setRows(await getSectionWisePaidSummary(fromDate, toDate, byShiftAndSection));
    } catch (err) {
      setError("Failed to load fee paid summary.");
    } finally {
      setLoading(false);
    }
  };

  const feeHeadNames = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => Object.keys(r.details || {}).forEach((h) => set.add(h)));
    return Array.from(set).sort();
  }, [rows]);

  const rowTotal = (r) => feeHeadNames.reduce((s, h) => s + (r.details[h] || 0), 0);
  const grandTotal = (rows || []).reduce((s, r) => s + rowTotal(r), 0);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Paid Summary</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <label>
          Group By
          <select value={byShiftAndSection ? "1" : "0"} onChange={(e) => setByShiftAndSection(e.target.value === "1")}>
            <option value="0">Class Wise</option>
            <option value="1">Class-Shift-Section Wise</option>
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        rows && (
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Section</th>
                  {feeHeadNames.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.classConfigId}>
                    <td>{r.sectionName}</td>
                    {feeHeadNames.map((h) => (
                      <td key={h}>{(r.details[h] || 0).toFixed(2)}</td>
                    ))}
                    <td>{rowTotal(r).toFixed(2)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={feeHeadNames.length + 2}>No paid invoices found for this selection.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ fontWeight: 800 }} colSpan={feeHeadNames.length + 1}>
                    Grand Total
                  </td>
                  <td>
                    <strong>{grandTotal.toFixed(2)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}
    </MainLayout>
  );
}
